import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { vehicleTypePageRouteName } from "../list/index.page";
import {
  useVehicleTypeServiceV1GetVehicleTypeQuery,
  useVehicleTypeServiceV1UpdateVehicleTypeMutation,
} from "_services/modules/vehicle-type";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface EditVehicleTypeFormValues {
  vehicleType: string;
}

const schema = yup.object({
  vehicleType: yup.string().required(),
}).required();

const useEditVehicleTypeForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleType");

  const { data: detail, isLoading } = useVehicleTypeServiceV1GetVehicleTypeQuery(
    { id: id! },
    { skip: !id }
  );

  const [updateVehicleType] = useVehicleTypeServiceV1UpdateVehicleTypeMutation();

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    reset,
  } = useForm<EditVehicleTypeFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      vehicleType: "",
    },
  });

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] = useState<EditVehicleTypeFormValues | null>(null);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  useEffect(() => {
    if (detail?.vehicle_type) {
      reset({ vehicleType: detail.vehicle_type });
    }
  }, [detail, reset]);

  const onSubmit = (data: EditVehicleTypeFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      await updateVehicleType({
        id,
        vehicleTypeServiceV1UpdateVehicleTypeBody: {
          vehicle_type: pendingData.vehicleType,
        },
      }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      setShowSaveModal(false);
      if (error?.status === 409) {
        setError("vehicleType", { type: "validate", message: t("error-duplicate-exists") });
      } else {
        errorHandler(error);
      }
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    handleSubmit,
    errors,
    register,
    setError,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
  };
};

export default useEditVehicleTypeForm;
